import { parseMessageId } from "./messaging-identity.js";
// The pure rules of the Stream message projection and of the AI reply trigger (CHT-726; docs/architecture/milestone-c-contracts.md
// sections 4.3 and 6; ADRs 0010 and 0011). No I/O. The database guards (`conversation_message_refs`, `conversation_replies`)
// repeat the allowed moves; a database test asserts the parity.
//
// Frozen: the database CHECK constraints are generated from these tuples, and `@lafayette/schemas` repeats the reply states (a parity
// test in `@lafayette/agent-contract` fails when one differs).
// --- message references ---------------------------------------------------------------------------------------------------------
/** What Postgres knows of one Stream message: ids, state and times. NEVER the text. */
export const messageRefStates = Object.freeze(["PRESENT", "EDITED", "DELETED"]);
/** Who wrote a message, from the Stream user id. Copilot messages live in a private channel the webhook ignores. */
export const messageRefAuthorKinds = Object.freeze([
    "CUSTOMER",
    "LAFAYETTE_AI",
    "CONCIERGE",
    "SYSTEM",
]);
/** The ids of a Stream message that fit the column check `^[A-Za-z0-9:_.-]{1,64}$`. Another shape is never stored. */
export const MESSAGE_REF_ID_PATTERN = /^[A-Za-z0-9:_.-]{1,64}$/;
const later = (a, b) => (a.getTime() >= b.getTime() ? a : b);
/**
 * Applies one webhook event to the reference of its message. The projection keeps the NEWEST state by event time (Stream does not
 * promise order), so a `message.updated` that arrives before its `message.new` creates the reference, and a `message.new` that
 * arrives after a delete does not bring the message back. A duplicate is a no-op because the state is the same.
 */
export function applyMessageEvent(current, event) {
    const edited = event.editedAt ?? event.eventAt;
    const deleted = event.deletedAt ?? event.eventAt;
    if (current === null) {
        const base = { sentAt: event.sentAt, lastEventAt: event.eventAt };
        switch (event.kind) {
            case "new":
            case "undeleted":
                return {
                    snapshot: { ...base, state: "PRESENT", editedAt: null, deletedAt: null },
                    stale: false,
                };
            case "updated":
                return {
                    snapshot: { ...base, state: "EDITED", editedAt: edited, deletedAt: null },
                    stale: false,
                };
            case "deleted":
                return {
                    snapshot: { ...base, state: "DELETED", editedAt: null, deletedAt: deleted },
                    stale: false,
                };
        }
    }
    // `message.new` describes the creation. It never changes a state, but it moves the newest-event time.
    if (event.kind === "new") {
        return {
            snapshot: { ...current, lastEventAt: later(current.lastEventAt, event.eventAt) },
            stale: false,
        };
    }
    if (event.eventAt.getTime() < current.lastEventAt.getTime()) {
        // An older event never changes the state. One fact is kept anyway: "this message was edited" does not go away, so a late edit is
        // remembered and shows after an undelete (the order of arrival must not decide it).
        if (event.kind === "updated") {
            const editedAt = current.editedAt === null ? edited : later(current.editedAt, edited);
            const state = current.state === "PRESENT" ? "EDITED" : current.state;
            return { snapshot: { ...current, state, editedAt }, stale: true };
        }
        return { snapshot: current, stale: true };
    }
    const lastEventAt = event.eventAt;
    switch (event.kind) {
        case "updated":
            // An edit of a deleted message changes nothing (the delete is the newest fact about it).
            return current.state === "DELETED"
                ? { snapshot: { ...current, lastEventAt }, stale: false }
                : {
                    snapshot: { ...current, state: "EDITED", editedAt: edited, lastEventAt },
                    stale: false,
                };
        case "deleted":
            return {
                snapshot: { ...current, state: "DELETED", deletedAt: deleted, lastEventAt },
                stale: false,
            };
        case "undeleted":
            return {
                snapshot: {
                    ...current,
                    state: current.editedAt === null ? "PRESENT" : "EDITED",
                    deletedAt: null,
                    lastEventAt,
                },
                stale: false,
            };
    }
}
// --- conversation replies -------------------------------------------------------------------------------------------------------
/**
 * One row per eligible customer message (unique by source message). `PENDING` and `RUNNING` are open. The webhook writes `PENDING`
 * (or `CAPPED` when the daily cap is reached, so no run starts); the reply executor (CHT-727) moves the rest.
 */
export const conversationReplyStates = Object.freeze([
    "PENDING",
    "RUNNING",
    "COMPLETED",
    "DEGRADED",
    "FAILED",
    "INTERRUPTED",
    "FENCED",
    "CAPPED",
    "EXPIRED",
    "SKIPPED",
]);
export const conversationReplyOpenStates = Object.freeze(["PENDING", "RUNNING"]);
export const conversationReplyTerminalStates = Object.freeze(conversationReplyStates.filter((state) => state !== "PENDING" && state !== "RUNNING"));
/** The only moves. A terminal reply never moves; only its Stream message bookkeeping (`ai_message_finalized_at`) is written afterwards. */
export const conversationReplyTransitions = Object.freeze({
    PENDING: Object.freeze(["RUNNING", "SKIPPED", "EXPIRED", "CAPPED"]),
    RUNNING: Object.freeze(["COMPLETED", "DEGRADED", "FAILED", "INTERRUPTED", "FENCED"]),
    COMPLETED: Object.freeze([]),
    DEGRADED: Object.freeze([]),
    FAILED: Object.freeze([]),
    INTERRUPTED: Object.freeze([]),
    FENCED: Object.freeze([]),
    CAPPED: Object.freeze([]),
    EXPIRED: Object.freeze([]),
    SKIPPED: Object.freeze([]),
});
export function isConversationReplyTransitionAllowed(from, to) {
    return conversationReplyTransitions[from].includes(to);
}
/** The states a reply row may be INSERTED in: waiting for the executor, or ended by the daily cap before any run. */
export const conversationReplyInitialStates = Object.freeze(["PENDING", "CAPPED"]);
// --- the AI trigger gate --------------------------------------------------------------------------------------------------------
/** A customer message older than this when its webhook arrives is projected but never triggers a reply (contract 4.3). */
export const AI_TRIGGER_STALE_SOURCE_MS = 600_000;
export const aiTriggerRefusals = Object.freeze([
    "NOT_NEW_MESSAGE",
    "NOT_CUSTOMER_AUTHOR",
    "NOT_CUSTOMER_CHANNEL",
    "THREAD_REPLY",
    "MESSAGE_ID_UNSUPPORTED",
    "MESSAGE_DELETED",
    "STALE_SOURCE",
    // D-A4: the text is empty or only white space. The reply executor reads text only, so such a message could never be answered; it
    // would end `FAILED/SOURCE_UNAVAILABLE`, count in the run cap and tell the customer nothing.
    "EMPTY_TEXT",
    "REPLIES_OFF",
    "MODE_NOT_AI_ACTIVE",
    // CHT-734 (lane C, `decideAiAnswerToCustomerMessage`): the message was sent before the last mode change (a person held the
    // conversation when it was sent), or the generation is not the current one.
    "MESSAGE_BEFORE_RESUME",
    "GENERATION_STALE",
    // CHT-780 (the beta controls): an operator control keeps the AI away from this customer, so a person must answer (human-only mode).
    "AI_PAUSED",
    "NOT_IN_COHORT",
    "AI_CONTROL_UNREADABLE",
]);
/**
 * Whether a customer message may ask for an AI reply. ONE rule, in the order of the contract: only a `message.new` of the CUSTOMER
 * in a `messaging` channel, not a thread reply, with a Lafayette-shaped id (a lowercase uuid, which the clients set), not deleted, younger
 * than ten minutes, with a text that is not blank (an attachment does not count: the AI reads text only), with replies switched on, while the conversation is `AI_ACTIVE`. AI, system, staff and copilot messages never trigger,
 * so the AI cannot answer itself. The first refusal is returned. This function does not read the clock or Postgres.
 */
export function planAiTrigger(input, staleSourceMs = AI_TRIGGER_STALE_SOURCE_MS) {
    const refuse = (reason) => ({ eligible: false, reason });
    if (input.eventKind !== "new")
        return refuse("NOT_NEW_MESSAGE");
    if (input.authorKind !== "CUSTOMER")
        return refuse("NOT_CUSTOMER_AUTHOR");
    if (input.channelType !== "messaging")
        return refuse("NOT_CUSTOMER_CHANNEL");
    if (input.parentId !== null)
        return refuse("THREAD_REPLY");
    if (parseMessageId(input.messageId)?.kind !== "client")
        return refuse("MESSAGE_ID_UNSUPPORTED");
    if (input.refState === "DELETED")
        return refuse("MESSAGE_DELETED");
    if (input.receivedAt.getTime() - input.sentAt.getTime() >= staleSourceMs) {
        return refuse("STALE_SOURCE");
    }
    if (input.blankText === true)
        return refuse("EMPTY_TEXT");
    if (input.aiReplies !== "on")
        return refuse("REPLIES_OFF");
    if (input.mode !== "AI_ACTIVE")
        return refuse("MODE_NOT_AI_ACTIVE");
    return { eligible: true };
}
// --- the daily cap --------------------------------------------------------------------------------------------------------------
/** The start of the UTC calendar day of `instant`, and of the next one. The cap window is a UTC day of `agent_runs.started_at`. */
export function utcDayBounds(instant) {
    const from = new Date(Date.UTC(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate()));
    return { from, to: new Date(from.getTime() + 86_400_000) };
}
/**
 * The ids of the two agents that write `agent_runs` rows in a customer conversation (one rule for every query that scopes by agent:
 * the reply executor's busy check and the reply claim SQL name the concierge, the copilot's own caps name the copilot).
 * `@lafayette/agent-contract` and `@lafayette/schemas` carry the same strings; parity tests pin them.
 */
export const CUSTOMER_CONCIERGE_AGENT_ID = "customer-concierge";
export const STAFF_COPILOT_RUN_AGENT_ID = "staff-copilot";
/** CHT-733 review: one staff member may have at most this many copilot runs RUNNING at once (across conversations). */
export const STAFF_COPILOT_MAX_CONCURRENT_RUNS = 2;
/** CHT-733 review: the default of the per-staff daily cap of copilot runs (`LAFAYETTE_AI_STAFF_DAILY_RUN_CAP`). */
export const STAFF_COPILOT_DEFAULT_DAILY_RUN_CAP = 100;
/**
 * The longest message the private copilot channel accepts (`max_message_length` of the `staff_copilot` channel type, set by
 * `scripts/stream-configure.mjs`; a test pins the two numbers together). The history writer cuts a longer text to this length.
 */
export const COPILOT_CHANNEL_MAX_MESSAGE_CHARS = 5000;
/**
 * `OK` while the customer has room for one more run today. `RUN_CAP` or `TOKEN_CAP` once the runs or the tokens (input plus output)
 * of the UTC day have reached the cap. Every run counts, failed and cancelled ones included.
 */
export function evaluateAiDailyCap(usage, caps) {
    if (usage.runs >= caps.runs)
        return "RUN_CAP";
    if (usage.tokens >= caps.tokens)
        return "TOKEN_CAP";
    return "OK";
}
// --- the reply executor (CHT-727) -----------------------------------------------------------------------------------------------
/**
 * The timings of the reply executor (ADR 0010). `@lafayette/agent-contract` freezes the same numbers as `CONVERSATION_REPLY_TIMINGS`;
 * the application cannot import that package, so a parity test (`milestone-c-parity.test.ts`) fails when one differs.
 */
export const REPLY_EXECUTOR_TIMINGS = Object.freeze({
    /** A claim holds a lease that long. The executor renews it. */
    leaseMs: 90_000,
    heartbeatMs: 20_000,
    /** A PENDING reply older than this is not started any more: it ends EXPIRED with a failure message. */
    startDeadlineMs: 120_000,
    /** The least time between two Stream updates of one AI message (the final update is always sent). */
    minUpdateIntervalMs: 500,
    staleSourceMs: AI_TRIGGER_STALE_SOURCE_MS,
    /** The sweeper marks a RUNNING reply INTERRUPTED when its lease ended more than this long ago. */
    sweepLeaseGraceMs: 30_000,
    /** After SIGTERM the executor has this long to finalize its running replies (Cloud Run gives 10 s by default). */
    shutdownBudgetMs: 8_000,
});
/** The history window of a reply run. `@lafayette/agent-contract` freezes the same numbers as `CONVERSATION_HISTORY_LIMITS_V1`. */
export const REPLY_HISTORY_LIMITS = Object.freeze({
    maxTurns: 20,
    maxChars: 8_000,
    maxTurnChars: 2_000,
});
/** The most characters of AI text in one Stream message; the text is cut there. */
export const AI_MESSAGE_MAX_TEXT_CHARS = 4_000;
